import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Home, Bookmark, LineChart, FileText, LogOut, Globe } from 'lucide-react';
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from './ui/sheet';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';

/**
 * Premium left-slide drawer menu for mobile. Replaces the former bottom nav.
 * Desktop users keep their inline top nav in Header.js — this component only
 * renders on mobile (`md:hidden` on the trigger button).
 */
const MobileDrawerMenu = () => {
  const { user, logout } = useAuth();
  const { t, language, switchLanguage } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const close = () => setOpen(false);

  const path = location.pathname;
  const search = location.search;
  const isSavedRoute = path.startsWith('/saved');
  const isTrackerActive = isSavedRoute && search.includes('view=tracker');
  const isSavedActive = isSavedRoute && !isTrackerActive;

  const items = [
    {
      key: 'home', to: '/dashboard', label: t('navHome'), icon: Home,
      active: path === '/' || path.startsWith('/dashboard') || path.startsWith('/new-request') || path.startsWith('/results'),
    },
    { key: 'saved', to: '/saved', label: t('shortcutSavedTitle'), icon: Bookmark, active: isSavedActive },
    { key: 'tracker', to: '/saved?view=tracker', label: t('shortcutTrackerTitle'), icon: LineChart, active: isTrackerActive },
    { key: 'templates', to: '/templates', label: t('shortcutTemplatesTitle'), icon: FileText, active: path.startsWith('/templates') },
  ];

  const go = (to) => {
    close();
    navigate(to);
  };

  const handleLogout = async () => {
    close();
    await logout();
    navigate('/login');
  };

  const initials = (user?.name || user?.email || '?').trim().charAt(0).toUpperCase();

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          type="button"
          className="md:hidden -ml-1.5 p-2 rounded-md text-slate-600 hover:bg-slate-100 active:bg-slate-200 transition-colors"
          aria-label={t('openMenu')}
          data-testid="mobile-drawer-trigger"
        >
          <Menu className="w-5 h-5" strokeWidth={2} />
        </button>
      </SheetTrigger>

      <SheetContent
        side="left"
        className="p-0 w-[84vw] max-w-[320px] bg-slate-900 text-slate-100 border-r-0 [&>button]:hidden flex flex-col"
        data-testid="mobile-drawer"
      >
        <SheetTitle className="sr-only">{t('openMenu')}</SheetTitle>
        <SheetDescription className="sr-only">SafeSourcer navigation</SheetDescription>
        {/* Header row — profile + close */}
        <div className="px-5 pt-5 pb-4 border-b border-slate-800">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center font-semibold text-base shrink-0" data-testid="mobile-drawer-avatar">
                {initials}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold truncate" data-testid="mobile-drawer-name">{user?.name || t('navAccount')}</p>
                <p className="text-xs text-slate-400 truncate" data-testid="mobile-drawer-email">{user?.email}</p>
              </div>
            </div>
            <button
              type="button"
              onClick={close}
              className="p-1.5 -mr-1.5 rounded-md text-slate-400 hover:text-white hover:bg-slate-800"
              aria-label={t('close') || 'Close'}
              data-testid="mobile-drawer-close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Nav items */}
        <nav className="flex-1 overflow-y-auto py-3" data-testid="mobile-drawer-nav">
          <ul className="px-2 space-y-0.5">
            {items.map(({ key, to, label, icon: Icon, active }) => (
              <li key={key}>
                <Link
                  to={to}
                  onClick={close}
                  className={`flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium transition-colors ${
                    active
                      ? 'bg-blue-600/15 text-white ring-1 ring-inset ring-blue-500/30'
                      : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                  }`}
                  data-testid={`mobile-drawer-link-${key}`}
                >
                  <Icon className={`w-5 h-5 ${active ? 'text-blue-400' : 'text-slate-400'}`} strokeWidth={active ? 2.2 : 1.75} />
                  <span>{label}</span>
                </Link>
              </li>
            ))}
          </ul>

          {/* Language */}
          <div className="mt-5 px-5">
            <p className="text-[10px] uppercase tracking-[0.12em] text-slate-500 mb-2 flex items-center gap-1.5">
              <Globe className="w-3 h-3" /> {t('language')}
            </p>
            <div className="inline-flex items-center gap-1 bg-slate-800/60 border border-slate-700 rounded-lg p-1" data-testid="mobile-drawer-lang">
              <button
                onClick={() => switchLanguage('fr')}
                className={`px-4 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  language === 'fr' ? 'bg-blue-600 text-white' : 'text-slate-300 hover:text-white'
                }`}
                data-testid="mobile-drawer-lang-fr"
              >
                FR
              </button>
              <button
                onClick={() => switchLanguage('en')}
                className={`px-4 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  language === 'en' ? 'bg-blue-600 text-white' : 'text-slate-300 hover:text-white'
                }`}
                data-testid="mobile-drawer-lang-en"
              >
                EN
              </button>
            </div>
          </div>
        </nav>

        {/* Footer: logout */}
        <div className="px-4 pb-[calc(env(safe-area-inset-bottom)+16px)] pt-3 border-t border-slate-800">
          <button
            type="button"
            onClick={handleLogout}
            className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-300 hover:bg-slate-800 hover:text-white transition-colors"
            data-testid="mobile-drawer-logout"
          >
            <LogOut className="w-4 h-4" />
            {t('logout')}
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default MobileDrawerMenu;
